import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/RoPriceReferencing/MaxPrice';
import { validateHistoricProductDataViewDate } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	ErrorCode,
	Product,
	ProductUpdate,
	RoMaxPrice as ApiRoMaxPrice,
	Update,
	ValidationResult,
} from '~/Api/User/ProductGroup/RoMaxPrice';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { formatNumericHumanDate } from '~/DateTime';
import { parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import Calculation from '~/RoMaxPrice/Calculation/CalculationContainer';
import ParametersPanel, { Values as ReferenceValues } from '~/RoMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/RoMaxPrice/ProductsTable/ProductsTable';
import ActivityIndicator from '~/UI/ActivityIndicator';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceValue } from './ProductsTable/Row/Row';
import { ProductGroupShowAllItemsSwitch } from '~/libs/ui/atoms/ProductGroupShowAllItemsSwitch';

import styles from './styles.less';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';
import Icon from '~/UI/Icon';
import { TailwindModal } from '~/libs/ui/atoms/TailwindModal';
import { ButtonCancel } from '~/pages/IRPDetail/src/components/ButtonCancel';
import { Typography } from '~/libs/ui/atoms/Typography';

interface Props {
	romanianProducts: ProductRow[];
	currency: string | null;
	detailProductId: string | null;
	detailProductTabName: string;
	group: UserProductGroup;
	header: ReactNode;
	roMaxPrice: ApiRoMaxPrice | null;
	isFetching: boolean;
	isAdditionalPriceRoofAllowed: boolean;
	otherProducts: ProductRow[];
	alternativeProductsCount: number;
	pricedProducts: Array<PricedProduct<Product>>;
	referenceBasketVersion: ReferenceBasketVersion | null;
	referencedProducts: ProductRow[];
	validationResult: ValidationResult | null;
	onCloseDetail(): void;
	onCurrencyChange(currency: string): void;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	onSave(group: UserProductGroup): Promise<void>;
	onShowDetail(productId: string | null, tabName: string): void;
	onUpdate(group: UserProductGroup, update: Update): void;
	onResetProductList(groupId: string): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onDeleteItems(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
}

interface Values {
	product: { [productId: string]: ProductValues };
	reference: ReferenceValues;
}

interface State {
	lastSeenProductIdDetail: string | null;
	lastSelectedProductId: string | null;
	resetProductList: boolean;
	removeProduct: GeneralProduct | null;
	values: Values | null;
	checkedRows: GeneralProduct[];
}

export default class RoMaxPrice extends Component<Props, State> {
	public static defaultProps = {
		detailProductId: null,
		detailProductTabName: 'info',
		roMaxPrice: null,
		validationResult: null,
	};

	public state: State = {
		lastSeenProductIdDetail: null,
		lastSelectedProductId: null,
		resetProductList: false,
		removeProduct: null,
		values: null,
		checkedRows: [],
	};

	private readonly createValues = memoizeOne((roMaxPrice: ApiRoMaxPrice | null, values: Values | null): Values => {
		const historicProductDataViewDate = roMaxPrice?.reference.historicProductDataViewDate ?? null;

		return {
			product:
				roMaxPrice?.products.reduce((products: { [productId: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					if (currentValues !== undefined) {
						products[product.product.id] = currentValues;
						return products;
					}

					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);
					products[product.product.id] = {
						packageSize: {
							isValid: true,
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						},
						packageSizeForSingleUnit: {
							isValid: true,
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
						},
						referencePrice: {
							isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
							value:
								referencePrice !== undefined
									? {
											value: referencePrice.calculatedPrice.type,
											parentValue: null,
											prompt: null,
									  }
									: null,
						},
						selected: product.selected,
					};
					return products;
				}, {}) ?? {},
			reference: values?.reference ?? {
				basketVersion: roMaxPrice?.reference.referenceBasketVersion ?? '',
				decisionNumber: {
					value: roMaxPrice?.reference.decisionNumber ?? '',
					isValid: true,
				},
				fxUpdatePrice: {
					value: roMaxPrice?.reference.fxUpdatePrice ? String(roMaxPrice.reference.fxUpdatePrice) : '',
					isValid: true,
				},
				fxUpdatePriceEnabled: roMaxPrice?.reference.fxUpdatePriceEnabled ?? false,
				packageSize: {
					value: String(roMaxPrice?.reference.packageSize ?? ''),
					isValid: true,
				},
				prgPrbPrice: {
					value: roMaxPrice?.reference.prgPrbPrice ? String(roMaxPrice.reference.prgPrbPrice) : '',
					isValid: true,
				},
				prgPrbPriceEnabled: roMaxPrice?.reference.prgPrbPriceEnabled ?? false,
				priPrice: {
					value: roMaxPrice?.reference.priPrice ? String(roMaxPrice.reference.priPrice) : '',
					isValid: true,
				},
				priPriceEnabled: roMaxPrice?.reference.priPriceEnabled ?? false,
				lossOfExclusivity: roMaxPrice?.reference.lossOfExclusivity ?? '',
				gtin: roMaxPrice?.reference.gtin ?? '',
				productType: roMaxPrice?.reference.referencedProductType ?? '',
				historicProductDataViewDate: {
					value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
					dateValue: historicProductDataViewDate,
					isValid: true,
				},
			},
		};
	});

	public componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	public componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	public componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	private showResetProductListModal = () => {
		this.setState({ resetProductList: true });
	};

	public render() {
		const {
			props: {
				header,
				roMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				romanianProducts,
				referencedProducts,
				otherProducts,
				alternativeProductsCount,
				isFetching,
				isAdditionalPriceRoofAllowed,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onResetProductList,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, resetProductList, removeProduct, checkedRows },
		} = this;

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;

		if (roMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId !== null ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider id={detailProductId} roMaxPrice={roMaxPrice}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => {
											onShowDetail(productId, detailProductTabName);
										}}
										onSelectTab={(tabName) => {
											onShowDetail(detailProductId, tabName);
										}}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{roMaxPrice !== null && referenceBasketVersion !== null ? (
						<Calculation
							roMaxPrice={roMaxPrice}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
						/>
					) : null}

					<ParametersPanel
						detectedDecisionNumber={roMaxPrice?.finalCalculation.decisionNumber ?? ''}
						group={group}
						isAdditionalPriceRoofAllowed={isAdditionalPriceRoofAllowed}
						roMaxPrice={roMaxPrice ?? null}
						values={values.reference}
						onChange={this.referenceValuesChanged}
						onSave={() => {
							onSave(group);
						}}
					/>

					{roMaxPrice?.products.length === 0 ? <ProductInstructions /> : null}

					{(roMaxPrice?.products.length || 0) > 0 ? (
						<>
							<div className='w-full'>
								<div className='w-full max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-wrap justify-between items-end'>
									<div className='w-full relative flex items-center justify-between z-1'>
										<Typography level={2} weight='font-light'>
											Romanian products
										</Typography>

										<div className='pl-3 text-right'>
											<TailwindTooltip
												reference={<Icon name='violet-question-mark' />}
												displayClass='inline-block -mt-px mr-2 h-4 align-middle'
												position='left'
											>
												<p className='leading-snug'>
													Clicking this button will automatically refresh prices, validities and other product details
													based on module-specific settings. Refreshing product list will overwrite all existing{' '}
													<strong>Use for calc.</strong> field settings and cannot be reverted.
												</p>
											</TailwindTooltip>
											<button
												onClick={() => {
													this.showResetProductListModal();
												}}
												className='inline-block px-2.5 py-1.5 rounded text-sm leading-snug font-semibold text-white bg-red-600 align-middle transition hover:bg-red-800'
											>
												Refresh product list
											</button>
										</div>
									</div>
								</div>
							</div>
							<ProductsTable
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={romanianProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={this.handleProductValuesChange}
								onSave={() => {
									onSave(group);
								}}
								onSelect={this.productSelected}
							/>
							<div className='max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-wrap justify-between items-end'>
								<Typography level={2} weight='font-light' className='pr-4'>
									Reference basket products
								</Typography>
								<div className='grow shrink pt-1 pb-0.5 text-right'>
									<ProductGroupShowAllItemsSwitch alternativeProductsCount={alternativeProductsCount} />
								</div>
							</div>
						</>
					) : null}

					{referencedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={referencedProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{otherProducts.length > 0 ? (
						<>
							<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
								<Typography level={2} weight='font-light'>
									Other
								</Typography>
							</div>
							<ProductsTable
								disableSelection
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={otherProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={this.handleProductValuesChange}
								onSave={() => {
									onSave(group);
								}}
								onSelect={this.productSelected}
							/>
						</>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => {
								this.setState({ removeProduct: null });
							}}
							onConfirm={async () => {
								await onSave(group);
							}}
							onSuccess={() => {
								this.setState({ removeProduct: null });
							}}
						/>
					) : null}
					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}
				</section>
				{resetProductList ? (
					<TailwindModal
						footer={
							<div className='w-full flex items-center justify-center'>
								<ButtonCancel
									onClick={() => {
										this.setState({ resetProductList: false });
									}}
								>
									Close
								</ButtonCancel>
								<button className='inline-block px-2.5 py-1.5 rounded text-sm leading-snug font-semibold text-white bg-red-600 align-middle transition hover:bg-red-800'>
									Refresh product list
								</button>
							</div>
						}
						title={`Refresh product list: ${group.name}`}
						onClose={() => {
							this.setState({ resetProductList: false });
						}}
						onSubmit={() => {
							this.setState({ resetProductList: false });
							this.setState({ values: null });
							onResetProductList(this.props.group.id);
						}}
					>
						<p className='leading-snug text-center'>
							Do you want to refresh product list?
							<br />
							Continuing with this operation will also overwrite all existing <strong>Use for calc.</strong> field
							settings. <br />
							This cannot be reverted.
						</p>
					</TailwindModal>
				) : null}
			</Fragment>
		);
	}

	private readonly handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private readonly handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice.value?.value !== referencePrice?.value;

		this.setState(
			{
				values: {
					product: {
						...values.product,
						[item.product.id]: {
							packageSize: { value: packageSize, isValid: null },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							referencePrice: {
								value: referencePrice,
								isValid: item.referencePrices.length === 0 || referencePrice !== null,
							},
							selected: currentValue.selected,
						},
					},
					reference: values.reference,
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		const invokeSave =
			reference.basketVersion !== values.reference.basketVersion ||
			reference.productType !== values.reference.productType;

		this.setState({ values: { ...values, reference } }, () => {
			this.update();
			if (invokeSave) {
				this.props.onSave(this.props.group);
			}
		});
	};

	private processValidationResult(validationResult: ValidationResult | null) {
		if (validationResult === null) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referencePackageSizeIsMissing,
			},
		};
		const productValues = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			if (error.productId === null || !(error.productId in productValues)) {
				return;
			}
			const values = productValues[error.productId];
			if (error.code === ErrorCode.requiredProductSettingsMissing) {
				productValues[error.productId] = {
					...values,
					packageSize: { ...values.packageSize, isValid: false },
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.illegalPriceTypeSelected) {
				productValues[error.productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private readonly productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				lastSelectedProductId: item.product.id,
				values: { reference: values.reference, product: productValues },
			},
			this.update
		);
	};

	private resolveValues(): Values {
		const {
			props: { roMaxPrice },
			state: { values },
		} = this;
		return this.createValues(roMaxPrice, values);
	}

	private selectProductRange(
		productValues: { [productId: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	private readonly update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		const referenceFxUpdatePrice = parsePackageSize(referenceValues.fxUpdatePrice.value);
		referenceValues.fxUpdatePrice.isValid = referenceFxUpdatePrice.isValid;

		const referencePrgPrbPrice = parsePackageSize(referenceValues.prgPrbPrice.value);
		referenceValues.prgPrbPrice.isValid = referencePrgPrbPrice.isValid;

		referenceValues.historicProductDataViewDate.isValid =
			referenceValues.historicProductDataViewDate.value === '' ||
			(referenceValues.historicProductDataViewDate.dateValue !== null &&
				validateHistoricProductDataViewDate(referenceValues.historicProductDataViewDate.dateValue));

		let hasValidationError =
			referencePackageSize.isValid === false ||
			referenceValues.historicProductDataViewDate.isValid === false ||
			referenceValues.fxUpdatePrice.isValid === false ||
			referenceValues.prgPrbPrice.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			hasValidationError =
				hasValidationError ||
				!referencePrice.isValid ||
				parsedPackageSize.isValid === false ||
				parsedPackageSizeForSingleUnit.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
			};

			products.push(
				new ProductUpdate(
					productId,
					referencePrice.value?.value ?? null,
					parsedPackageSize.value,
					parsedPackageSizeForSingleUnit.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(this.props.group, {
				decisionNumber: referenceValues.decisionNumber.value === '' ? null : referenceValues.decisionNumber.value,
				historicProductDataViewDate: referenceValues.historicProductDataViewDate.dateValue,
				packageSize: referencePackageSize.value,
				products,
				referenceBasketVersion: referenceValues.basketVersion,
				referencedProductType: referenceValues.productType === '' ? null : referenceValues.productType,
				fxUpdatePrice: referenceValues.fxUpdatePrice.value === '' ? null : Number(referenceValues.fxUpdatePrice.value),
				fxUpdatePriceEnabled: referenceValues.fxUpdatePriceEnabled,
				prgPrbPrice: referenceValues.prgPrbPrice.value === '' ? null : Number(referenceValues.prgPrbPrice.value),
				prgPrbPriceEnabled: referenceValues.prgPrbPriceEnabled,
				priPrice: referenceValues.priPrice.value === '' ? null : Number(referenceValues.priPrice.value),
				priPriceEnabled: referenceValues.priPriceEnabled,
				lossOfExclusivity: referenceValues.lossOfExclusivity,
				gtin: referenceValues.gtin,
			});
		}
	};
}
